import React, { useState, useEffect } from 'react';
import {
    FaPlus, FaEye, FaEdit, FaTrash, FaTimes,
    FaFilePdf, FaDownload, FaSearch, FaUsers,
    FaClipboardList, FaChartBar, FaCheckCircle, FaCheck,
    FaClock, FaSpinner, FaChevronDown, FaChevronUp,
    FaProjectDiagram, FaCalendarAlt, FaMoneyBillWave,
    FaBuilding, FaUser, FaEnvelope, FaFileAlt, FaFilter
} from 'react-icons/fa';
import { Calendar } from 'lucide-react';
import { getAllEnquiries } from '../../../Servicefile/salesService';

// Date filter options
const dateFilterOptions = ["All", "Today", "Yesterday", "This Week", "This Month", "Last Month", "This Year", "Custom Range"];

// Status filter options
const statusFilterOptions = [
    "ASSIGNED",
    "PENDING",
    "APPROVED",
    "PAYMENT_RECEIVED",
    "ACCEPT",
    "ACCEPTED",
    "CLOSED",
    "REJECTED"
];

const PurchaseOrders = () => {
    // ============================================
    // STATE
    // ============================================
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Filter states
    const [selectedDateFilter, setSelectedDateFilter] = useState("All");
    const [showDateDropdown, setShowDateDropdown] = useState(false);
    const [customFromDate, setCustomFromDate] = useState(() => {
        const date = new Date();
        date.setDate(1);
        return date.toISOString().split('T')[0];
    });
    const [customToDate, setCustomToDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });
    const [showCustomPicker, setShowCustomPicker] = useState(false);
    const dateDropdownRef = React.useRef(null);

    // Status filter - Default "ASSIGNED"
    const [selectedStatusFilter, setSelectedStatusFilter] = useState("ASSIGNED");
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const statusDropdownRef = React.useRef(null);

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        assigned: 0,
        pending: 0,
        approved: 0,
        paymentReceived: 0,
        paymentReceivedBudget: 0,  // Add this

        closed: 0,
        rejected: 0
    });

    // ============================================
    // API CALLS
    // ============================================
    const fetchLeads = async () => {
        setLoading(true);
        try {
            const data = await getAllEnquiries();
            setLeads(data || []);
            calculateStats(data || []);
        } catch (error) {
            console.error('Error fetching leads:', error);
            alert('Failed to fetch leads');
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (data) => {
        const total = data.length;
        const assigned = data.filter(e => e.leadStatus === 'ASSIGNED').length;
        const pending = data.filter(e => e.leadStatus === 'PENDING').length;
        const approved = data.filter(e => e.leadStatus === 'APPROVED').length;
        const paymentReceived = data.filter(e => e.leadStatus === 'PAYMENT_RECEIVED').length;
        const closed = data.filter(e => e.leadStatus === 'CLOSED').length;
        const rejected = data.filter(e => e.leadStatus === 'REJECTED').length;

        // Calculate total budget for each status
        const paymentReceivedBudget = data
            .filter(e => e.leadStatus === 'ASSIGNED')
            .reduce((sum, e) => sum + (e.budget || 0), 0);

        setStats({
            total,
            assigned,
            pending,
            approved,
            paymentReceived,
            paymentReceivedBudget,  // Add this
            closed,
            rejected
        });
    };

    // ============================================
    // DATE FILTER FUNCTIONS
    // ============================================
    const getDateRangeFromSelection = () => {
        if (selectedDateFilter === "All") return null;

        const today = new Date();
        let from = new Date();
        let to = new Date();

        switch (selectedDateFilter) {
            case "Today":
                from = today;
                to = today;
                break;
            case "Yesterday":
                from.setDate(today.getDate() - 1);
                to.setDate(today.getDate() - 1);
                break;
            case "This Week": {
                const day = today.getDay();
                from.setDate(today.getDate() - day + (day === 0 ? -6 : 1));
                to = new Date(from);
                to.setDate(from.getDate() + 6);
                break;
            }
            case "This Month":
                from = new Date(today.getFullYear(), today.getMonth(), 1);
                to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case "Last Month":
                from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                to = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case "This Year":
                from = new Date(today.getFullYear(), 0, 1);
                to = new Date(today.getFullYear(), 11, 31);
                break;
            case "Custom Range":
                return { fromDate: customFromDate, toDate: customToDate };
            default:
                return null;
        }

        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            fromDate: formatDate(from),
            toDate: formatDate(to),
        };
    };

    const filterByDateRange = (items) => {
        const range = getDateRangeFromSelection();
        if (!range) return items;

        const startDate = new Date(range.fromDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(range.toDate);
        endDate.setHours(23, 59, 59, 999);

        return items.filter(item => {
            const createdDate = item.createdAt ? new Date(item.createdAt) : null;
            if (!createdDate) return false;
            return createdDate >= startDate && createdDate <= endDate;
        });
    };

    const handleDateFilterSelect = (filter) => {
        setSelectedDateFilter(filter);
        if (filter === "Custom Range") {
            setShowCustomPicker(true);
        } else {
            setShowCustomPicker(false);
            setShowDateDropdown(false);
        }
    };

    const applyCustomRange = () => {
        setShowCustomPicker(false);
        setShowDateDropdown(false);
    };

    const getDateFilterDisplayText = () => {
        if (selectedDateFilter === "All") return "All";
        if (selectedDateFilter === "Custom Range") {
            return `${customFromDate} to ${customToDate}`;
        }
        return selectedDateFilter;
    };

    // ============================================
    // STATUS FILTER FUNCTIONS
    // ============================================
    const handleStatusFilterSelect = (status) => {
        setSelectedStatusFilter(status);
        setShowStatusDropdown(false);
    };

    const getStatusDisplayName = (status) => {
        const names = {
            'ASSIGNED': 'Assigned',
            'PENDING': 'Pending',
            'APPROVED': 'Approved',
            'PAYMENT_RECEIVED': 'Payment Received',
            'ACCEPT': 'Accept',
            'ACCEPTED': 'Accepted',
            'CLOSED': 'Closed',
            'REJECTED': 'Rejected'
        };
        return names[status] || status;
    };

    // ============================================
    // CLOSE DROPDOWNS
    // ============================================
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
                setShowDateDropdown(false);
                setShowCustomPicker(false);
            }
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ============================================
    // APPLY FILTERS
    // ============================================
    useEffect(() => {
        let result = leads;

        // Apply date filter
        result = filterByDateRange(result);

        // Apply status filter
        if (selectedStatusFilter) {
            result = result.filter(item => item.leadStatus === selectedStatusFilter);
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(item =>
                (item.fullName || '').toLowerCase().includes(term) ||
                (item.email || '').toLowerCase().includes(term) ||
                (item.companyName || '').toLowerCase().includes(term) ||
                (item.city || '').toLowerCase().includes(term) ||
                (item.enquiryId || item.id || '').toString().includes(term)
            );
        }

        setFilteredLeads(result);
    }, [leads, selectedDateFilter, customFromDate, customToDate, selectedStatusFilter, searchTerm]);

    // ============================================
    // EFFECTS
    // ============================================
    useEffect(() => {
        fetchLeads();
    }, []);

    // ============================================
    // FORMAT HELPERS
    // ============================================
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return `₹${Number(amount).toLocaleString('en-IN')}`;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'PENDING': { bg: '#FEF3C7', color: '#92400E', label: 'Pending' },
            'APPROVED': { bg: '#D1FAE5', color: '#065F46', label: 'Approved' },
            'PAYMENT_RECEIVED': { bg: '#DBEAFE', color: '#1E40AF', label: 'Payment Received' },
            'ASSIGNED': { bg: '#E0E7FF', color: '#3730A3', label: 'Assigned' },
            'ACCEPT': { bg: '#FCE7F3', color: '#BE185D', label: 'Accept' },
            'ACCEPTED': { bg: '#D1FAE5', color: '#065F46', label: 'Accepted' },
            'CLOSED': { bg: '#F3F4F6', color: '#4B5563', label: 'Closed' },
            'REJECTED': { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' }
        };
        const info = statusMap[status] || statusMap['PENDING'];
        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: 600,
                background: info.bg,
                color: info.color
            }}>
                {info.label}
            </span>
        );
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setSelectedDateFilter('All');
        setSelectedStatusFilter('ASSIGNED');
        setCustomFromDate(() => {
            const date = new Date();
            date.setDate(1);
            return date.toISOString().split('T')[0];
        });
        setCustomToDate(new Date().toISOString().split('T')[0]);
    };

    return (
        <div style={containerStyle}>


            {/* Stats Cards */}
            <div style={statsContainerStyle}>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#EEF2FF' }}>
                        <FaUsers style={{ color: '#4F46E5' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.total}</div>
                        <div style={statLabelStyle}>Total Leads</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#E0E7FF' }}>
                        <FaUser style={{ color: '#3730A3' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.assigned}</div>
                        <div style={statLabelStyle}>Assigned</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#FEF3C7' }}>
                        <FaClock style={{ color: '#D97706' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.pending}</div>
                        <div style={statLabelStyle}>Pending</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#D1FAE5' }}>
                        <FaCheckCircle style={{ color: '#059669' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.approved}</div>
                        <div style={statLabelStyle}>Approved</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#DBEAFE' }}>
                        <FaMoneyBillWave style={{ color: '#1E40AF' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.paymentReceived}</div>
                        <div style={statLabelStyle}>Payment Received</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#F3F4F6' }}>
                        <FaCheck style={{ color: '#4B5563' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.closed}</div>
                        <div style={statLabelStyle}>Closed</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#FEE2E2' }}>
                        <FaTimes style={{ color: '#DC2626' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{stats.rejected}</div>
                        <div style={statLabelStyle}>Rejected</div>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <div style={{ ...statIconWrapperStyle, background: '#DBEAFE' }}>
                        <FaMoneyBillWave style={{ color: '#1E40AF' }} size={20} />
                    </div>
                    <div>
                        <div style={statNumberStyle}>{formatCurrency(stats.paymentReceivedBudget)}</div>
                        <div style={statLabelStyle}>Payment Received</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div style={filterContainerStyle}>
                {/* Search */}
                <div style={searchContainerStyle}>
                    <FaSearch style={searchIconStyle} />
                    <input
                        type="text"
                        placeholder="Search leads..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={searchInputStyle}
                    />
                    {searchTerm && (
                        <button onClick={() => setSearchTerm('')} style={clearSearchButtonStyle}>
                            <FaTimes size={14} />
                        </button>
                    )}
                </div>

                {/* Date Filter */}
                <div ref={dateDropdownRef} style={{ position: "relative" }}>
                    <button
                        onClick={() => setShowDateDropdown(!showDateDropdown)}
                        style={{
                            ...dateFilterBtnStyle,
                            ...(showDateDropdown ? dateFilterBtnActiveStyle : {})
                        }}
                    >
                        <Calendar size={14} color={showDateDropdown ? '#e66d33' : '#64748b'} />
                        <span>{getDateFilterDisplayText()}</span>
                        <FaChevronDown size={12} color={showDateDropdown ? '#e66d33' : '#64748b'} />
                    </button>

                    {showDateDropdown && !showCustomPicker && (
                        <div style={dropdownMenuStyle}>
                            {dateFilterOptions.map(option => (
                                <div
                                    key={option}
                                    onClick={() => handleDateFilterSelect(option)}
                                    style={{
                                        ...dropdownItemStyle,
                                        ...(selectedDateFilter === option ? dropdownItemActiveStyle : {})
                                    }}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}

                    {showCustomPicker && (
                        <div style={customPickerStyle}>
                            <div style={{ marginBottom: '12px' }}>
                                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', color: '#64748b' }}>From Date</label>
                                <input
                                    type="date"
                                    value={customFromDate}
                                    onChange={(e) => setCustomFromDate(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                    max={customToDate}
                                />
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', color: '#64748b' }}>To Date</label>
                                <input
                                    type="date"
                                    value={customToDate}
                                    onChange={(e) => setCustomToDate(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                                    min={customFromDate}
                                    max={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                            <button
                                onClick={applyCustomRange}
                                style={{ width: '100%', padding: '10px', background: '#e66d33', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}
                            >
                                Apply Range
                            </button>
                        </div>
                    )}
                </div>

                {/* Status Filter */}
                <div ref={statusDropdownRef} style={{ position: "relative" }}>
                    <button
                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                        style={{
                            ...statusFilterBtnStyle,
                            ...(showStatusDropdown ? statusFilterBtnActiveStyle : {})
                        }}
                    >
                        <FaFilter size={14} color={showStatusDropdown ? '#e66d33' : '#64748b'} />
                        <span>Status: {getStatusDisplayName(selectedStatusFilter)}</span>
                        <FaChevronDown size={12} color={showStatusDropdown ? '#e66d33' : '#64748b'} />
                    </button>

                    {showStatusDropdown && (
                        <div style={dropdownMenuStyle}>
                            {statusFilterOptions.map(status => (
                                <div
                                    key={status}
                                    onClick={() => handleStatusFilterSelect(status)}
                                    style={{
                                        ...dropdownItemStyle,
                                        ...(selectedStatusFilter === status ? dropdownItemActiveStyle : {})
                                    }}
                                >
                                    {getStatusDisplayName(status)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Clear Filters */}
                <button
                    onClick={clearAllFilters}
                    style={clearButtonStyle}
                >
                    <FaTimes size={14} /> Clear
                </button>

            </div>

            {/* Table */}
            <div style={tableCardStyle}>
                {loading ? (
                    <div style={loadingStyle}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={32} color="#4F46E5" />
                        <p>Loading leads...</p>
                    </div>
                ) : (
                    <div style={tableWrapperStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: '4%', textAlign: 'center' }}>#</th>
                                    <th style={{ ...thStyle, width: '10%', textAlign: 'left' }}>Customer</th>
                                    <th style={{ ...thStyle, width: '8%', textAlign: 'center' }}>Source</th>
                                    <th style={{ ...thStyle, width: '8%', textAlign: 'center' }}>Source By</th>
                                    <th style={{ ...thStyle, width: '8%', textAlign: 'center' }}>Event</th>
                                    <th style={{ ...thStyle, width: '10%', textAlign: 'center' }}>Date</th>
                                    <th style={{ ...thStyle, width: '8%', textAlign: 'center' }}>City</th>
                                    <th style={{ ...thStyle, width: '10%', textAlign: 'center' }}>Budget</th>
                                    <th style={{ ...thStyle, width: '12%', textAlign: 'center' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLeads.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" style={emptyStateStyle}>
                                            <div style={emptyStateContentStyle}>
                                                <FaUsers size={40} color="#D1D5DB" />
                                                <p>No leads found</p>
                                                <span style={{ fontSize: '13px', color: '#9CA3AF' }}>
                                                    {searchTerm ? 'Try adjusting your search terms' : 'No leads match your filters'}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredLeads.map((lead, index) => (
                                        <tr key={lead.id || lead.enquiryId || index} style={tableRowStyle}>
                                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 500, color: '#9CA3AF' }}>
                                                {index + 1}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'left' }}>
                                                <div style={customerCellStyle}>
                                                    <div style={avatarStyle}>
                                                        {(lead.fullName || '?')[0].toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                                            {lead.fullName || '-'}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                                            {lead.email || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <span style={sourceBadgeStyle}>
                                                    {lead.source || '-'}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {lead.sourceBy || '-'}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <span style={eventBadgeStyle}>
                                                    {lead.eventType || '-'}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center', fontSize: '12px', color: '#374151' }}>
                                                {lead.event === 'EVENT'
                                                    ? formatDate(lead.eventDate)
                                                    : `${formatDate(lead.fromDate)} - ${formatDate(lead.toDate)}`}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <span style={cityBadgeStyle}>
                                                    {lead.city || '-'}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: '#059669' }}>
                                                {formatCurrency(lead.budget)}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {getStatusBadge(lead.leadStatus)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

// ─── Styles ──────────────────────────────────────────────────────

const containerStyle = {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    flexWrap: 'wrap',
    gap: '16px',
};

const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
};

const headerIconStyle = {
    width: '44px',
    height: '44px',
    background: 'linear-gradient(135deg, #e66d33, #e66d33)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
};

const titleStyle = {
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    letterSpacing: '-0.5px',
};

const subtitleStyle = {
    fontSize: '14px',
    color: '#6B7280',
    margin: '4px 0 0 0',
};

// Stats Styles
const statsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '10px',
};

const statCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#fff',
    padding: '14px 16px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #E5E7EB',
    transition: 'all 0.2s ease',
};

const statIconWrapperStyle = {
    width: '38px',
    height: '38px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const statNumberStyle = {
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
    lineHeight: 1.2,
};

const statLabelStyle = {
    fontSize: '10px',
    color: '#6B7280',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
};

// Filter Styles
const filterContainerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
};

const searchContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    minWidth: '200px',
    background: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '0 14px',
    transition: 'all 0.2s ease',
};

const searchIconStyle = {
    color: '#9CA3AF',
    marginRight: '10px',
};

const searchInputStyle = {
    flex: 1,
    padding: '10px 0',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
    color: '#111827',
};

const clearSearchButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9CA3AF',
    padding: '4px 6px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
};

const dateFilterBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    height: '38px',
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#334155',
};

const dateFilterBtnActiveStyle = {
    borderColor: '#e66d33',
    color: '#e66d33',
};

const statusFilterBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    height: '38px',
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#334155',
};

const statusFilterBtnActiveStyle = {
    borderColor: '#e66d33',
    color: '#e66d33',
};

const dropdownMenuStyle = {
    position: 'absolute',

    left: 0,
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    zIndex: 1000,
    minWidth: '180px',
    maxHeight: "200px",
    scrollbarWidth: "none",
    overflow: 'auto',
};

const dropdownItemStyle = {
    padding: '10px 16px',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderBottom: '1px solid #f0f0f0',
};

const dropdownItemActiveStyle = {
    background: '#fef3e8',
    color: '#e66d33',
};

const customPickerStyle = {
    position: 'absolute',
    top: '45px',
    left: 0,
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    zIndex: 1000,
    width: '280px',
    padding: '16px',
};

const clearButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    height: '38px',
    background: '#f1f5f9',
    color: '#4B5563',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const resultCountStyle = {
    fontSize: '13px',
    color: '#6B7280',
    fontWeight: 500,
    whiteSpace: 'nowrap',
};

// Table Styles
const tableCardStyle = {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    border: '1px solid #E5E7EB',
};

const tableWrapperStyle = {
    overflowX: 'auto',
    padding: '0',
    maxHeight: '60vh',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1100px',
};

const thStyle = {
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    background: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
    position: 'sticky',
    top: 0,
    zIndex: 10,
};

const tdStyle = {
    padding: '12px 16px',
    fontSize: '13px',
    color: '#111827',
    borderBottom: '1px solid #F3F4F6',
};

const tableRowStyle = {
    transition: 'background 0.15s ease',
    cursor: 'default',
};

const customerCellStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
};

const avatarStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #e66d33, #d45a2a)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 600,
    flexShrink: 0,
};

const sourceBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    background: '#F3F4F6',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#4B5563',
};

const eventBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    background: '#F3F4F6',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#4B5563',
};

const cityBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    background: '#EEF2FF',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#4F46E5',
};

// Empty State
const emptyStateStyle = {
    padding: '60px 20px',
    textAlign: 'center',
    borderBottom: 'none',
};

const emptyStateContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
};

const loadingStyle = {
    padding: '60px 20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: '#6B7280',
};

export default PurchaseOrders;